/**
 * GitHub API Functions
 * Functions for workflow dispatch and status tracking
 */

import { getEchoConfig } from '../config/echo';
import type { GitHubWorkflowRun, WorkflowDispatchInput, GitHubWorkflowJob } from './types/echo';

/**
 * Dispatch a GitHub Actions workflow
 * @returns true if dispatch was successful (204 response)
 */
export async function dispatchWorkflow(
  inputs: WorkflowDispatchInput
): Promise<boolean> {
  const config = getEchoConfig();
  console.log('[GitHub] Config:', { ...config, GITHUB_TOKEN: config.GITHUB_TOKEN ? `${config.GITHUB_TOKEN.slice(0, 10)}...` : 'MISSING' });

  const url = `https://api.github.com/repos/${config.GITHUB_OWNER}/${config.GITHUB_REPO}/actions/workflows/${config.GITHUB_WORKFLOW}/dispatches`;
  console.log('[GitHub] Dispatching to:', url);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${config.GITHUB_TOKEN}`,
      Accept: 'application/vnd.github+json',
    },
    body: JSON.stringify({
      ref: config.GITHUB_REF,
      inputs,
    }),
  });

  console.log('[GitHub] Response status:', response.status);
  if (response.status !== 204) {
    const text = await response.text();
    console.log('[GitHub] Error:', text);
  }

  return response.status === 204;
}

/**
 * Get a specific workflow run by ID
 */
export async function getWorkflowRun(
  runId: number
): Promise<GitHubWorkflowRun | null> {
  const config = getEchoConfig();

  const url = `https://api.github.com/repos/${config.GITHUB_OWNER}/${config.GITHUB_REPO}/actions/runs/${runId}`;

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${config.GITHUB_TOKEN}`,
        Accept: 'application/vnd.github+json',
      },
    });

    if (!response.ok) {
      console.error(`Failed to get workflow run: ${response.status}`);
      return null;
    }

    const data = await response.json();
    return {
      id: data.id,
      run_number: data.run_number,
      status: data.status,
      conclusion: data.conclusion,
      html_url: data.html_url,
      created_at: data.created_at,
    };
  } catch (error) {
    console.error('Error fetching workflow run:', error);
    return null;
  }
}

/**
 * Find a recently dispatched workflow run
 * Looks for runs created after the given dispatch time
 */
export async function findRecentDispatchedRun(
  dispatchTime: Date,
  maxAttempts = 10,
  delayMs = 2000
): Promise<GitHubWorkflowRun | null> {
  const config = getEchoConfig();

  const url = `https://api.github.com/repos/${config.GITHUB_OWNER}/${config.GITHUB_REPO}/actions/runs?per_page=5`;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${config.GITHUB_TOKEN}`,
          Accept: 'application/vnd.github+json',
        },
      });

      if (!response.ok) {
        console.error(`Failed to list workflow runs: ${response.status}`);
        await sleep(delayMs);
        continue;
      }

      const data = await response.json();
      const runs = data.workflow_runs || [];

      // Find a run that was created after our dispatch time
      for (const run of runs) {
        const runCreatedAt = new Date(run.created_at);
        // Allow 30 second buffer for timing differences
        if (runCreatedAt.getTime() >= dispatchTime.getTime() - 30000) {
          return {
            id: run.id,
            run_number: run.run_number,
            status: run.status,
            conclusion: run.conclusion,
            html_url: run.html_url,
            created_at: run.created_at,
          };
        }
      }

      // Wait before next attempt
      await sleep(delayMs);
    } catch (error) {
      console.error('Error finding recent run:', error);
      await sleep(delayMs);
    }
  }

  return null;
}

/**
 * Check if a workflow run is in a terminal state
 */
export function isTerminalStatus(run: GitHubWorkflowRun): boolean {
  return (
    run.status === 'completed' ||
    run.conclusion !== null ||
    run.status === 'failure' ||
    run.status === 'cancelled'
  );
}

/**
 * Check if a workflow run completed successfully
 */
export function isSuccessful(run: GitHubWorkflowRun): boolean {
  return run.status === 'completed' && run.conclusion === 'success';
}

/**
 * Check if a workflow run failed
 */
export function isFailed(run: GitHubWorkflowRun): boolean {
  return (
    run.status === 'completed' &&
    (run.conclusion === 'failure' || run.conclusion === 'cancelled')
  );
}

/**
 * Check if a workflow is still running
 */
export function isRunning(run: GitHubWorkflowRun): boolean {
  return run.status === 'in_progress' || run.status === 'queued';
}

/**
 * Get a display-friendly status string
 */
export function getStatusDisplay(run: GitHubWorkflowRun): string {
  if (run.status === 'queued') return 'Queued';
  if (run.status === 'in_progress') return 'Running';
  if (run.conclusion === 'success') return 'Completed';
  if (run.conclusion === 'failure') return 'Failed';
  if (run.conclusion === 'cancelled') return 'Cancelled';
  return run.status;
}

// Helper function
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * List workflow runs with optional status filter
 */
export async function listWorkflowRuns(
  perPage = 10,
  status?: 'queued' | 'in_progress' | 'completed'
): Promise<{ runs: GitHubWorkflowRun[]; error: string | null }> {
  const config = getEchoConfig();

  let url = `https://api.github.com/repos/${config.GITHUB_OWNER}/${config.GITHUB_REPO}/actions/runs?per_page=${perPage}`;
  if (status) {
    url += `&status=${status}`;
  }

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${config.GITHUB_TOKEN}`,
        Accept: 'application/vnd.github+json',
      },
    });

    if (!response.ok) {
      return { runs: [], error: `Failed to fetch workflow runs: ${response.status}` };
    }

    const data = await response.json();
    const runs: GitHubWorkflowRun[] = (data.workflow_runs || []).map((run: Record<string, unknown>) => ({
      id: run.id as number,
      run_number: run.run_number as number,
      status: run.status as string,
      conclusion: run.conclusion as string | null,
      html_url: run.html_url as string,
      created_at: run.created_at as string,
      updated_at: run.updated_at as string,
      display_title: run.display_title as string,
      actor: (run.actor as Record<string, unknown>)?.login as string || 'unknown',
      inputs: (run as Record<string, unknown>).inputs as Record<string, string> || {},
    }));

    return { runs, error: null };
  } catch (error) {
    console.error('Error listing workflow runs:', error);
    return { runs: [], error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Get detailed workflow run including inputs
 */
export async function getWorkflowRunDetails(
  runId: number
): Promise<{ run: GitHubWorkflowRun | null; error: string | null }> {
  const config = getEchoConfig();

  const url = `https://api.github.com/repos/${config.GITHUB_OWNER}/${config.GITHUB_REPO}/actions/runs/${runId}`;

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${config.GITHUB_TOKEN}`,
        Accept: 'application/vnd.github+json',
      },
    });

    if (!response.ok) {
      return { run: null, error: `Failed to fetch run details: ${response.status}` };
    }

    const data = await response.json();
    const run: GitHubWorkflowRun = {
      id: data.id,
      run_number: data.run_number,
      status: data.status,
      conclusion: data.conclusion,
      html_url: data.html_url,
      created_at: data.created_at,
      updated_at: data.updated_at,
      display_title: data.display_title,
      actor: data.actor?.login || 'unknown',
      inputs: data.inputs || {},
    };

    return { run, error: null };
  } catch (error) {
    console.error('Error fetching run details:', error);
    return { run: null, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Get jobs for a workflow run
 */
export async function getWorkflowJobs(
  runId: number
): Promise<{ jobs: GitHubWorkflowJob[]; error: string | null }> {
  const config = getEchoConfig();

  const url = `https://api.github.com/repos/${config.GITHUB_OWNER}/${config.GITHUB_REPO}/actions/runs/${runId}/jobs`;

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${config.GITHUB_TOKEN}`,
        Accept: 'application/vnd.github+json',
      },
    });

    if (!response.ok) {
      return { jobs: [], error: `Failed to fetch jobs: ${response.status}` };
    }

    const data = await response.json();
    const jobs: GitHubWorkflowJob[] = (data.jobs || []).map((job: Record<string, unknown>) => ({
      id: job.id as number,
      name: job.name as string,
      status: job.status as string,
      conclusion: job.conclusion as string | null,
      started_at: job.started_at as string,
      completed_at: job.completed_at as string | null,
      steps: ((job.steps as Array<Record<string, unknown>>) || []).map((step) => ({
        name: step.name as string,
        status: step.status as string,
        conclusion: step.conclusion as string | null,
        number: step.number as number,
      })),
    }));

    return { jobs, error: null };
  } catch (error) {
    console.error('Error fetching workflow jobs:', error);
    return { jobs: [], error: error instanceof Error ? error.message : 'Unknown error' };
  }
}
